import { DataService, DeletionResult, DuplicateEntityDialogComponent, ItemOf, ModalService, NotificationService, registerBulkAction } from '@vendure/admin-ui/core';
import {
    addNavMenuItem,
} from '@vendure/admin-ui/core';
import { Router } from '@angular/router';
import { MutationDeletePageArgs, PageList } from '../common/ui/generated';
import { DELETE_PAGE,  EXPORT_PAGE, DUPLICATE_PAGE_WITH_OPTIONS  } from './common/ui-types';
import { map, take } from 'rxjs/operators';
import { DuplicatePageOptions, PageDuplicateOptionsDialogComponent } from './components/duplicate-page-options-dialog.component';
import { PageType } from '../common/ui/ui-types';

type GetPageListQuery = {
  pages: PageList;
};

function isProductOrCollectionPage(page): boolean {
    return (
        page.type === PageType.ProductSpecific ||
        page.type === PageType.CollectionSpecific
    );
}

function copyPages(context: any) {
    const modalService = context.injector.get(ModalService);
    const dataService = context.injector.get(DataService);
    const notificationService = context.injector.get(NotificationService);

    const selection = context.selection as Array<
        ItemOf<GetPageListQuery, 'pages'>
    >;

    if (!selection.length) {
        return;
    }

    // MULTI: just duplicate as-is, no relation form
    if (selection.length > 1 || !isProductOrCollectionPage(selection[0])) {
        modalService
        .fromComponent(DuplicateEntityDialogComponent<ItemOf<GetPageListQuery, 'pages'>>, {
            locals: {
                entities: context.selection as any[],
                entityName: 'Page',
                title: "Duplicate " + (selection.length === 1 ? "a Page" : "Pages"),
                getEntityName: (entity: any) => entity?.name,
            },
        })
        .subscribe(async (response) => {
            if(response) {
                context.clearSelection();
                context.hostComponent.refresh();
            }
        });
        return;
    }

    // SINGLE product/collection-specific page → open options dialog
    const page: any = selection[0];

    modalService
        .fromComponent(PageDuplicateOptionsDialogComponent, {
            locals: { page },
        })
        .subscribe((options: DuplicatePageOptions | undefined) => {
            if (!options) {
                return; // cancelled
            }
            dataService
                .mutate(DUPLICATE_PAGE_WITH_OPTIONS, {
                    pageId: page.id,
                    productId: options.productId,
                    collectionId: options.collectionId,
                })
                .pipe(take(1))
                .subscribe({
                    next: (res) => {
                        if (res?.duplicatePageWithOptions?.id) {
                            notificationService.success('Page duplicated');
                        } else {
                            notificationService.error(
                                'Page duplication failed on server',
                            );
                        }
                        context.clearSelection();
                        context.hostComponent.refresh();
                    }
                });
        });
}

function deletePages(context: any) {
    const modalService = context.injector.get(ModalService);
    modalService
    .dialog({
        title: `Delete ${context.selection.length} pages ?`,
        buttons: [
        { type: 'secondary', label: 'cancel' },
        { type: 'primary', label: 'OK', returnValue: true },
        ],
    })
    .subscribe(async (response) => {
        if(response) {
            const pageIds = context.selection.map(item => item.id);
            const notificationService = context.injector.get(NotificationService);
            const dataService = context.injector.get(DataService);
            pageIds.forEach(async pageId => {
                const input = { input: { id: pageId} } as MutationDeletePageArgs;
                dataService.mutate(DELETE_PAGE, input)
                .pipe(map((result:any) => result.deletePage))
                .subscribe(
                    (deletePage:any) => {
                        if(deletePage.result === DeletionResult.DELETED) {
                            notificationService.info(`Page deleted`);
                        } else {
                            notificationService.error(`Page deletion failed: ${deletePage.message}`);
                        }
                        context.clearSelection();
                        context.hostComponent.refresh();
                    }
                );
            });
        }
    });
}

async function exportPage(context: any) {
    const modalService = context.injector.get(ModalService);
    modalService
    .dialog({
        title: `Export ${context.selection[0].name} page ?`,
        buttons: [
        { type: 'secondary', label: 'cancel' },
        { type: 'primary', label: 'OK', returnValue: true },
        ],
    })
    .subscribe(async (response) => {
        if (response) {
        const pageId = context.selection[0].id;
        const dataService = context.injector.get(DataService);
        const notificationService = context.injector.get(NotificationService);
        await exportPageProcess(pageId, dataService, notificationService);
        }
    });
}
async function exportPageProcess(pageId: string, dataService: DataService, notificationService: NotificationService) {
    dataService.mutate(EXPORT_PAGE, { id: pageId })
    .pipe(take(1)).subscribe(
        (data:any) => {
            if (data && data.exportPage) {
                const exportPage = data.exportPage;
                // Create file and trigger download
                const fileName = `page-${pageId}-${new Date().toISOString().substring(0, 10)}.json`;
                const jsonStr = JSON.stringify(exportPage, null, 2);
                const blob = new Blob([jsonStr], { type: 'application/json' });

                // Create download link and trigger click
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);

                notificationService.success('Page exported successfully');
            }
        },
        err => {
            notificationService.error('Failed to export page: ' + err.message);
        }
    );
}

export default [
    registerBulkAction({
        location: 'page-list',
        label: 'Duplicate Pages',
        icon: 'copy',
        requiresPermission: 'CreatePage',
        onClick: async (context) => {
            copyPages(context);
        },
    }),

    registerBulkAction({
        location: 'page-list',
        label: 'Export Page To Template File',
        icon: 'export',
        requiresPermission: 'ReadPage',
        isVisible: (context) => context.selection.length === 1,
        onClick: async (context) => {
            exportPage(context);
        },
    }),

    registerBulkAction({
        location: 'page-list',
        label: 'Delete Pages',
        icon: 'trash',
        requiresPermission: 'DeletePage',
        onClick: async (context) => {
            deletePages(context);
        },
    }),
];
